export enum PriceUnit {
  Meter = "Meter",
  Piece = "Piece",
  SquareMeter = "SquareMeter",
  CubicMeter = "CubicMeter",
  Set = "Set",
  Pair = "Pair",
  Kilogram = "Kilogram",
  Liter = "Liter",
  Roll = "Roll",
  Package = "Package",
  Gebinde = "Gebinde",
  /** Informational only — price entries without a known unit. */
  Unknown = "Unknown",
}

export enum MaterialType {
  Alu = 0,
  Panel = 1,
  PianoHinge = 2,
  Other = 3,
}

export interface PriceParameters {
  workCost: number;
  pAlu: number;
  pPanel: number;
  pNKMat: number;
  pNebenzeit: number;
  pMarginMaterial: number;
  pMarginTotal: number;
  otherCost: number;
}

export interface IPriceDataInfo {
  /**
   * Artikelnummer (= Materialcode der Preisberechnung).
   * @searchable
   * @unique
   */
  name: string;
  /**
   * Bezeichnung.
   * @searchable
   * @default ""
   */
  description: string;
  /**
   * @searchable
   * @default ""
   */
  partCategory: string;
  /**
   * @number-type Float
   * @default 0
   */
  pricePurchase: number;
  /** @default "Unknown" */
  unit: PriceUnit;
  /** @default 0 */
  weightGramms: number;
  /**
   * @number-type Float
   * @default 0
   */
  workingTimeMinutes: number;
  /** @default 0 */
  countRivets: number;
  /** @default "" */
  customerName: string;
}

/** Price entries indexed by article number (`name`). */
export type PriceData = Record<string, IPriceDataInfo>;

export interface IPriceParameterValue {
  /** @number-type Float */
  value: number;
}

export interface MaterialInfo {
  code: string;
  amount: number;
  description: string;
  type: MaterialType;
  unit: PriceUnit;
}

export interface MaterialPosition extends MaterialInfo {
  price: number;
  total: number;
  work: number;
}

export interface AdditionalItemInput {
  articleId: string;
  articleNumber: string;
  name: string;
  purchasePrice: number;
  workUnits: number;
  count: number;
}

export interface AdditionalItemPosition extends AdditionalItemInput {
  materialTotal: number;
  workCost: number;
  total: number;
}

/** Preisbericht: fehlender Code oder fehlender DB-Eintrag (roh, vor Gruppierung). */
export interface PriceCalculationIssue {
  kind: "missing_material_code" | "missing_db_entry";
  code?: string;
  detail: string;
}

/** Zusammengefasste Meldungen (gleiche Art + Code + Bezeichnung). */
export interface PriceCalculationIssueGrouped {
  count: number;
  kind: "missing_material_code" | "missing_db_entry";
  code?: string;
  detail: string;
}

export interface PriceCalculation {
  material: MaterialPosition[];
  additionalItems: AdditionalItemPosition[];
  /** Gruppiert nach Bezeichnung (z. B. 8× gleiche Platte) */
  priceIssues: PriceCalculationIssueGrouped[];
  sumMaterial: number;
  additionalItemsMaterial: number;
  additionalItemsWorkCost: number;
  additionalItemsTotal: number;
  wastedPanel: number;
  wastedAluminium: number;
  amountMarginMaterial: number;
  amountMarginTotal: number;
  procurementCost: number;
  materialTotal: number;
  workTime: number;
  extraTime: number;
  additionalCost: number;
  priceNetto: number;
  priceBrutto: number;
}

export type MissingPriceBehavior = "warn" | "ignore" | "throw";

export interface PriceCalcOptions {
  vatRate?: number;
  rivetArticleCode?: string;
  rivetPriceDivisor?: number;
  onMissingPrice?: MissingPriceBehavior;
  additionalItems?: AdditionalItemInput[];
}

export type Vector2Like = { x: number; y: number } | [number, number];

export type Vector3Like = { x: number; y: number; z: number };

export interface PartLike {
  type: string;
  properties?: PartLike;
  dontShowInPlan?: boolean;
  isSupplyPart?: boolean;

  partSelection?: {
    name?: string;
    description?: string;
    strength?: number;
    inverted?: boolean;
    [key: string]: unknown;
  };

  color?: {
    codesByThickness?: Record<string, string>;
    name?: string;
    description?: string;
    [key: string]: unknown;
  };

  material?: {
    name?: string;
    description?: string;
    strength?: number;
    [key: string]: unknown;
  };

  size?: Vector2Like | [number, number];
  length?: number;
  strength?: number;
  panelType?: number | string;
  braked?: boolean;
  n?: number;
  points?: Vector3Like[];
  minimumOffset?: number;
  style?: string;
  [key: string]: unknown;
}
